// Схема шаблона протокола (003: research, Р2, Р10; FR-001, FR-002, FR-018). С 010 схемы живут
// в базе модуля documents с версиями и архивом; схемы раздела `protocols` файла установки —
// затравка (010: data-model.md, research, Р2). Мастер подключения добавляет к телу схемы
// число строк шапки, слова отметки выполнения, поправки вида строки и карту ролей колонок.
import { z } from '../kernel/zod.ts';
import { MeetingPolicySchema } from './status.ts';

/** Номер колонки листа, с 1 (A = 1). */
const ColumnSchema = z.number().int().min(1).max(64);

/**
 * Роли колонки для вопроса мастера (010, FR-009; docs/research/onboarding/roles.json).
 * В схему идут номер, работа, ответственный, срок, начало, подрядчик и примечание (FR-012).
 */
export const columnRoles = [
  'number',
  'work',
  'responsible',
  'start',
  'due',
  'actual_end',
  'delivery',
  'contractor',
  'comment',
  'quantity',
  'other',
] as const;
export type ColumnRole = (typeof columnRoles)[number];
export const ColumnRoleSchema = z.enum(columnRoles);

export const columnRoleLabels = {
  number: 'Номер',
  work: 'Работа',
  responsible: 'Ответственный',
  start: 'Начало',
  due: 'Срок',
  actual_end: 'Фактическое окончание',
  delivery: 'Поставка',
  // Владелец 26.10.2026: «Контрагент» вместо «Подрядчика» — по смыслу вернее.
  contractor: 'Контрагент',
  comment: 'Примечание',
  quantity: 'Количество',
  other: 'Другое',
} as const satisfies Record<ColumnRole, string>;

/** Колонка не используется схемой. */
export const UNUSED_COLUMN = 'unused';
export const ColumnRoleOrUnusedSchema = z.union([ColumnRoleSchema, z.literal(UNUSED_COLUMN)]);
export type ColumnRoleOrUnused = z.infer<typeof ColumnRoleOrUnusedSchema>;

/** Кто поставил роль или вид: правило, модель или администратор (принцип III). */
export const decidedBy = ['rule', 'ai', 'admin'] as const;
export type DecidedBy = (typeof decidedBy)[number];
export const DecidedBySchema = z.enum(decidedBy);

/** Роль колонки в карте схемы: для карточки и сравнения карт (FR-021; data-model.md). */
export const ColumnAssignmentSchema = z.object({
  role: ColumnRoleOrUnusedSchema,
  /** Подпись колонки как в шапке. */
  label: z.string().max(120),
  by: DecidedBySchema,
  /** Вероятность роли, если её поставила модель. */
  probability: z.number().min(0).max(1).optional(),
});
export type ColumnAssignment = z.infer<typeof ColumnAssignmentSchema>;

/** Вид строки в поправке (FR-023, FR-031): заголовок, пункт или подпункт. */
export const rowKindCorrections = ['header', 'item', 'subitem'] as const;
export type RowKindCorrectionKind = (typeof rowKindCorrections)[number];

/** Название строки для поправки: без учёта регистра и лишних пробелов (FR-031). */
export function rowNameKey(name: string): string {
  return name.replace(/\s+/gu, ' ').trim().toLocaleLowerCase('ru');
}

/**
 * Поправка вида строки (FR-023, FR-028, FR-031): заданная роль строки внутри ядра 1.7 на каждом
 * листе схемы. parent — название строки-родителя у подпункта: номера строк между листами
 * не совпадают. Ставит только администратор: модель о строении не спрашивают.
 */
export const RowKindCorrectionSchema = z.object({
  name: z.string().trim().min(1).max(200),
  kind: z.enum(rowKindCorrections),
  parent: z.string().trim().min(1).max(200).nullable().optional(),
  by: z.literal('admin'),
});
export type RowKindCorrection = z.infer<typeof RowKindCorrectionSchema>;

/** Поправок в одной схеме — не больше (data-model.md). */
export const ROW_KIND_CORRECTIONS_MAX = 500;

export const RowKindCorrectionsSchema = z
  .array(RowKindCorrectionSchema)
  .max(ROW_KIND_CORRECTIONS_MAX)
  .refine((list) => new Set(list.map((c) => rowNameKey(c.name))).size === list.length, {
    message: 'Название строки в поправках повторяется',
  });

/** Слова отметки выполнения по умолчанию (FR-046). */
export const DONE_WORDS_DEFAULT = [
  'выполнено',
  'выполнен',
  'выполнена',
  'готово',
  'готов',
  'готова',
];

export const TemplateSchemaSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  title: z.string(),
  /**
   * Признаки шапки: номер колонки → текст. Шапка узнана, если в одной из первых 10 строк
   * каждая названная колонка равна своему тексту без учёта регистра и лишних пробелов.
   * Равенство, а не вхождение: «Дата окончания» не узнаёт «Дату окончания по графику».
   */
  header: z.record(z.string().regex(/^\d+$/), z.string()).refine((h) => Object.keys(h).length >= 2),
  columns: z.object({
    number: ColumnSchema,
    text: ColumnSchema,
    responsible: ColumnSchema,
    due: ColumnSchema,
    start: ColumnSchema.optional(),
    contractor: ColumnSchema.optional(),
    comment: ColumnSchema.optional(),
  }),
  /** Слова паузы — подстроки без учёта регистра в названии или ячейке срока (FR-007). */
  pauseWords: z.array(z.string().min(1)).default(['приостанов', 'отложен']),
  /** Скрытые строки Excel не разбираются: разбор — только того, что видит человек. */
  skipHiddenRows: z.boolean().default(true),
  /** В ячейке срока пишут и начало — у протокола нет колонки «Дата начала» (FR-005). */
  startInDueCell: z.boolean().default(false),
  /** «Подрядчик» с этими подстроками (без учёта регистра) — не ответственный (FR-008). */
  contractorNotResponsible: z
    .array(z.string())
    .default(['делаем сами', 'своими силами', 'поиск подрядчика', 'биржа подряд', 'в работе']),
  /** Строк в шапке: 1 или 2; пункты читаются ниже последней (010, FR-032). */
  headerRows: z.union([z.literal(1), z.literal(2)]).default(1),
  /** Слова отметки выполнения: одно слово в ячейке срока или примечании закрывает пункт (FR-046). */
  doneWords: z.array(z.string().trim().min(1).max(40)).max(20).default(DONE_WORDS_DEFAULT),
  /** Поправки вида строки (010, FR-031). */
  rowKinds: RowKindCorrectionsSchema.default([]),
  /**
   * Карта ролей всех колонок варианта, в том числе «не используется»: номер колонки → роль
   * (010, data-model.md). У схем файла установки нет.
   */
  roles: z.record(z.string().regex(/^\d+$/), ColumnAssignmentSchema).optional(),
});
export type TemplateSchema = z.infer<typeof TemplateSchemaSchema>;

export const ProtocolsSettingsSchema = z.object({
  schemas: z.array(TemplateSchemaSchema).min(1),
  policy: MeetingPolicySchema,
});
export type ProtocolsSettings = z.infer<typeof ProtocolsSettingsSchema>;
